import type { Filter } from '@spendesk/bookkeeping-core-types';
import type * as NEA from '@spendesk/general-type-helpers/NonEmptyArray';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useMutation } from 'src/core/api/hooks/useMutation';

type SendMissingDocumentaryEvidencesReminderError =
  | {
      reason: 'invalidBodyParams';
      error: Error;
    }
  | { reason: 'unauthorized' | 'bookkeepingApiNotFound' | 'nothingToRemind' };

type SendMissingDocumentaryEvidencesReminderResponse = {
  reminderSent: true;
};

type SendMissingDocumentaryEvidencesReminderPayload = {
  userIds: NEA.NonEmptyArray<string>; // At least 1 element is required
  subject: string;
  body: string;
};

/**
 * Mutation
 **/

export const useSendMissingDocumentaryEvidencesReminder = () => {
  const companyId = useCompanyId();

  return useMutation<
    SendMissingDocumentaryEvidencesReminderPayload,
    SendMissingDocumentaryEvidencesReminderResponse,
    SendMissingDocumentaryEvidencesReminderResponse,
    SendMissingDocumentaryEvidencesReminderError
  >({
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint:
        '/documentary-evidence/send-missing-documentary-evidences-reminder',
      method: 'post',
    },
    options: {
      throwOnError: true,
      onSuccess: ({ client, payload }) => {
        const filters: Filter[] = [
          {
            field: 'bookkeepingStatus',
            operator: '=',
            value: ['toPrepare'],
          },
          {
            field: 'documentaryEvidenceStatus',
            operator: '=',
            value: ['missing'],
          },
          {
            field: 'requestor',
            operator: '=',
            value: payload.userIds,
          },
        ];

        client.invalidateQueries([
          'usePayablesQuery',
          companyId,
          { operator: 'and', subfilters: filters },
        ]);
      },
    },
    reshapeData(data) {
      return data;
    },
  });
};
